import React, { useCallback, useEffect, useRef, useState } from "react";
import MerchantsService from "../../../api/merchant";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha, useTheme } from "@mui/material/styles";

import { dateTimeMask } from "../../../common/dateMask";

// catalog_imports.status integers (mirror dispatcher.models.CatalogImports)
const STATUS = { CREATED: 0, IMPORTING: 1, SUCCESS: 2, ERROR: 3 };
const POLL_INTERVAL_MS = 3000;

export default function ImportStatusAndInfos({ ifood_id }) {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [importData, setImportData] = useState(null);

  const timerRef = useRef(null);
  const mountedRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const searchForImports = useCallback(async () => {
    try {
      const res = await MerchantsService.getMerchantsIfoodImport({ ifood_id });
      if (!mountedRef.current) return;
      const payload = Array.isArray(res.data) ? null : res.data;
      setImportData(payload);
      // Keep polling only while the import is still running.
      if (
        !payload ||
        payload.status === STATUS.SUCCESS ||
        payload.status === STATUS.ERROR
      ) {
        stopPolling();
      } else if (!timerRef.current) {
        timerRef.current = setInterval(searchForImports, POLL_INTERVAL_MS);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [ifood_id, stopPolling]);

  useEffect(() => {
    mountedRef.current = true;
    searchForImports();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [searchForImports, stopPolling]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "inherit" }}>
        <Stack spacing={2} sx={{ m: "auto" }}>
          <CircularProgress
            size={15}
            sx={{ "&:focus-visible": { border: "none" } }}
          />
        </Stack>
      </Box>
    );
  }

  const status = importData?.status;
  const percent =
    typeof importData?.progress_percent === "number"
      ? importData.progress_percent
      : 0;

  // Resolve colour + label from status.
  let color = theme.palette.error.main;
  let labelText = "Cardápio Não Importado";
  let showDate = false;

  if (status === STATUS.SUCCESS) {
    color = theme.palette.success.main;
    labelText = "Cardápio Importado";
    showDate = true;
  } else if (status === STATUS.IMPORTING) {
    color = theme.palette.warning.main;
    labelText = `Importando ${percent}%`;
  } else if (status === STATUS.CREATED) {
    color = theme.palette.warning.main;
    labelText = "Importação na fila…";
  } else if (status === STATUS.ERROR) {
    color = theme.palette.error.main;
    labelText = "Falha na importação";
    showDate = true;
  }

  const inProgress = status === STATUS.CREATED || status === STATUS.IMPORTING;

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          p: 1,
          ml: 2,
          borderRadius: 5,
          backgroundColor: alpha(color, 0.18),
        }}
      >
        {inProgress && <CircularProgress size={12} sx={{ color }} />}
        <Typography
          sx={{ flexShrink: 0, color, fontWeight: 600 }}
          variant="caption"
        >
          {labelText}
        </Typography>
      </Box>
      {showDate && importData?.created_at && (
        <Box
          sx={{
            p: 1,
            ml: 2,
            borderRadius: 5,
            backgroundColor: alpha(theme.palette.text.primary, 0.08),
          }}
        >
          <Typography sx={{ flexShrink: 0 }} variant="caption">
            Última Importação: {dateTimeMask(importData.created_at)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
